/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from './supabaseClient';

function wait(delay: number) {
  return new Promise(resolve => {
    setTimeout(resolve, delay);
  });
}

// Use absolute URL to avoid proxy issues
// export const BASE_API_URL = 'https://dovery-exhibition-backend.fly.dev/';

// export const BASE_API_URL = 'http://localhost:8001/';
// export const BASE_API_URL = 'http://dovery-exhibition-backend-app:8000/';
// export const BASE_API_URL = 'http://dvry-m9s-debian.local:8001/';
// export const BASE_API_URL = 'http://localhost:8001/';
const configuredBaseApiUrl = process.env.REACT_APP_API_URL || 'https://206.189.47.41:8002/';
export const BASE_API_URL = configuredBaseApiUrl.endsWith('/') ? configuredBaseApiUrl : `${configuredBaseApiUrl}/`;

type RequestMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

async function getResponseErrorMessage(response: Response): Promise<string> {
  const fallback = `HTTP ${response.status}: ${response.statusText}`;

  try {
    const body = await response.clone().json();
    const detail = body?.detail ?? body?.message ?? body?.error;

    if (typeof detail === 'string' && detail.trim()) {
      return `HTTP ${response.status}: ${detail}`;
    }

    if (Array.isArray(detail) && detail.length > 0) {
      return `HTTP ${response.status}: ${JSON.stringify(detail)}`;
    }
  } catch {
    // Fall through to text/fallback handling.
  }

  try {
    const text = await response.clone().text();
    if (text.trim()) {
      return `HTTP ${response.status}: ${text}`;
    }
  } catch {
    // Fall through to fallback.
  }

  return fallback;
}

async function request<T>(
  url: string,
  method: RequestMethod = 'GET',
  data: any = null,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();

  const headers: Record<string, string> = {
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined),
    ...extraHeaders,
  };

  const options: RequestInit = { 
    method, 
    headers,
    mode: 'cors',
  };

  if (data) {
    // Check if data is FormData - if so, don't stringify and don't add Content-Type
    if (data instanceof FormData) {
      options.body = data;
      // Don't set Content-Type for FormData - browser will set it with boundary
    } else {
      options.body = JSON.stringify(data);
      options.headers = {
        ...options.headers,
        'Content-Type': 'application/json; charset=UTF-8',
      };
    }
  }

  return wait(300)
    .then(() => fetch(BASE_API_URL + url, options))
    .then(async response => {
      if (response.status === 401) {
        // Token was rejected server-side — try to refresh it once
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          await supabase.auth.signOut();
          throw new Error('Session expired. Please log in again.');
        }

        // Refresh succeeded — retry the original request with the new token
        const { data: { session: newSession } } = await supabase.auth.getSession();
        const retryHeaders: Record<string, string> = {
          ...(options.headers as Record<string, string>),
          ...(newSession?.access_token ? { Authorization: `Bearer ${newSession.access_token}` } : {}),
          ...extraHeaders,
        };
        const retryOptions: RequestInit = { ...options, headers: retryHeaders };
        const retryResponse = await fetch(BASE_API_URL + url, retryOptions);
        if (!retryResponse.ok) {
          throw new Error(await getResponseErrorMessage(retryResponse));
        }
        return retryResponse.json();
      }

      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response));
      }

      return response.json();
    });
}

export const client = {
  get: <T>(url: string, headers?: Record<string, string>) => request<T>(url, 'GET', null, headers),
  post: <T>(url: string, data: any, headers?: Record<string, string>) => request<T>(url, 'POST', data, headers),
  patch: <T>(url: string, data: any, headers?: Record<string, string>) => request<T>(url, 'PATCH', data, headers || {}),
  put: <T>(url: string, data: any, headers?: Record<string, string>) => request<T>(url, 'PUT', data, headers || {}),
  delete: <T>(url: string, data: any) => request<T>(url, 'DELETE', data),
};
